/**
 * Les attelages, vus par l'écran — tout ce qui se calcule hors de React.
 *
 * **UNE AFFECTATION NE PORTE QU'UNE RESSOURCE.** En base, une ligne relie un
 * véhicule à un chauffeur OU à une remorque, jamais aux deux : c'est ce qui
 * permet de changer de remorque sans toucher au chauffeur. L'attelage qu'on
 * DESSINE — camion + remorque + chauffeur — est donc une composition faite ici,
 * et non une ligne qu'on lirait.
 *
 * **« EN COURS » NE VEUT PAS DIRE « SANS FIN »**, et c'est la règle la plus
 * facile à écrire de travers. Depuis qu'on peut PROGRAMMER un attelage, une
 * période qui commence lundi prochain n'a pas de fin non plus. En cours, c'est
 * celle qui COUVRE le jour dont on parle. Tout ce fichier s'appuie là-dessus,
 * jamais sur « la fin est vide ».
 *
 * Les dates circulent en `AAAA-MM-JJ`, la forme que l'API rend et attend. Elles
 * ne deviennent « JJ/MM/AAAA » qu'au dernier moment, pour être lues.
 */

import type { LigneParc } from './parc.js';
// Type SEUL, donc effacé à la compilation : les deux fichiers se citent l'un
// l'autre sans qu'aucun cycle n'existe à l'exécution.
import type { Indisponibilite } from './indisponibilite.js';

export type { Indisponibilite };

// ---------------------------------------------------------------------------
// Ce que l'API rend
// ---------------------------------------------------------------------------

/** Une période d'attelage, telle que l'API la rend. */
export interface Affectation {
  readonly id: string;
  readonly vehicule_id: string;
  /** L'immatriculation du véhicule, jointe par l'API. */
  readonly vehicule: string;
  readonly chauffeur_id: string | null;
  readonly remorque_id: string | null;
  /** Laquelle des deux ressources porte cette ligne. */
  readonly genre: 'chauffeur' | 'remorque';
  /** Le nom du chauffeur, ou l'immatriculation de la remorque. */
  readonly ressource: string;
  readonly du: string;
  readonly au: string | null;
  readonly note: string | null;
}

/** Ce qu'un véhicule tire et qui le conduit, à une date donnée. */
export interface Attelage {
  readonly vehicule: LigneParc;
  /** La période en cours du chauffeur, absente s'il n'y en a pas. */
  readonly chauffeur?: Affectation;
  readonly remorque?: Affectation;
}

/** Ce que l'échange rapide peut croiser. */
export type Quoi = 'chauffeurs' | 'remorques' | 'les_deux';

// ---------------------------------------------------------------------------
// Les dates
// ---------------------------------------------------------------------------

/**
 * Le jour d'aujourd'hui en `AAAA-MM-JJ`, dans le fuseau de la personne.
 *
 * **Pas `toISOString()`**, qui rendrait le jour à Greenwich : le 1er janvier à
 * 00 h 30 en France, il donnerait le 31 décembre. La faute est invisible
 * l'après-midi et sûre la nuit.
 */
export function aujourdhui(maintenant: Date = new Date()): string {
  const mois = String(maintenant.getMonth() + 1).padStart(2, '0');
  const jour = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}

/** « 2026-09-04 » → « 04/09/2026 ». Rend `null` si la date est illisible. */
export function enFrancais(iso: string | null): string | null {
  if (iso === null) return null;
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (morceaux === null) return null;
  return `${morceaux[3] as string}/${morceaux[2] as string}/${morceaux[1] as string}`;
}

/**
 * La date de fin à PROPOSER quand on décroche, en `AAAA-MM-JJ`.
 *
 * Le jour où l'on regarde, sauf si l'attelage n'a pas encore commencé — auquel
 * cas c'est son premier jour. Proposer le jour courant pour un attelage qui
 * débute la semaine prochaine donnerait une fin antérieure au début, que la
 * base refuse.
 *
 * **Écrite le 04/09/2026, après un défaut constaté sur le poste.** L'écran
 * posait la fin à la VEILLE du jour affiché, sans rien demander : décrocher le
 * jour même une remorque attelée le jour même échouait, et le seul contournement
 * était d'aller changer la date en haut de l'écran. La borne se saisit désormais,
 * et celle-ci n'est qu'une proposition.
 */
export function finProposee(du: string, jour: string): string {
  return du > jour ? du : jour;
}

/**
 * Ce qui empêche de clore un attelage à cette date, dit en français — ou `null`
 * si rien ne l'empêche.
 *
 * Ce contrôle ANTICIPE celui de la base, il ne le remplace pas : la contrainte
 * `affectations_periode_sensee` reste la seule autorité. Il existe pour que la
 * faute se lise sous le champ plutôt que dans un bandeau, après un aller-retour.
 */
export function refusFin(du: string, au: string): string | null {
  if (au === '') return 'Indiquez le dernier jour d’attelage.';
  if (au < du) {
    return `Le dernier jour d’attelage ne peut pas précéder le premier, le ${enFrancais(du) ?? du}.`;
  }
  return null;
}

/**
 * Le lendemain d'un jour, en `AAAA-MM-JJ`.
 *
 * Symétrique de `veille`, et calculée en UTC pour la même raison : on n'en tire
 * qu'une date, jamais une heure, et l'heure d'été ferait varier le résultat si
 * on passait par l'horloge locale.
 *
 * Sert à DIRE, pas à écrire : une période close le 4 laisse la ressource libre
 * le 5, et c'est ce 5 que le bandeau annonce. Aucune borne envoyée à l'API n'en
 * sort.
 */
export function lendemain(jour: string): string {
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  if (morceaux === null) return jour;
  const date = new Date(
    Date.UTC(
      Number(morceaux[1]),
      Number(morceaux[2]) - 1,
      Number(morceaux[3] as string) + 1,
    ),
  );
  return date.toISOString().slice(0, 10);
}

/**
 * La veille d'un jour, en `AAAA-MM-JJ`.
 *
 * C'est la date à laquelle une période se CLÔT quand une autre s'ouvre le
 * lendemain : les bornes sont inclusives des deux côtés, et clore le jour même
 * ferait se recouvrir les deux périodes — que la base refuse.
 *
 * Calculée en UTC pour que le passage à l'heure d'été n'ajoute ni ne retire un
 * jour ; on n'en tire que la date, jamais une heure.
 */
export function veille(jour: string): string {
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  if (morceaux === null) return jour;
  const date = new Date(
    Date.UTC(
      Number(morceaux[1]),
      Number(morceaux[2]) - 1,
      Number(morceaux[3] as string) - 1,
    ),
  );
  return date.toISOString().slice(0, 10);
}

/**
 * Cette période couvre-t-elle ce jour ?
 *
 * Les deux bornes sont INCLUSES — c'est ce que dit `daterange(du, au, '[]')` en
 * base. Une fin absente veut dire « pas de fin connue », donc oui dès lors que
 * le début est passé.
 */
export function couvre(
  periode: { readonly du: string; readonly au: string | null },
  jour: string,
): boolean {
  if (periode.du > jour) return false;
  return periode.au === null || periode.au >= jour;
}

/** « du 01/08/2026 au 31/08/2026 », ou « depuis le 01/08/2026 ». */
export function periodeDite(periode: {
  readonly du: string;
  readonly au: string | null;
}): string {
  const debut = enFrancais(periode.du) ?? periode.du;
  if (periode.au === null) return `depuis le ${debut}`;
  return `du ${debut} au ${enFrancais(periode.au) ?? periode.au}`;
}

// ---------------------------------------------------------------------------
// Composer les attelages
// ---------------------------------------------------------------------------

/**
 * L'attelage de chaque véhicule au jour dit.
 *
 * Un véhicule sans chauffeur ni remorque y figure quand même : c'est
 * précisément ce qu'on veut voir. Les véhicules DÉSACTIVÉS en sont écartés —
 * ils ne roulent plus, et leur place dans la bande des vignettes serait prise
 * pour rien.
 *
 * Deux lignes du même genre couvrant le même jour ne peuvent pas exister : la
 * base l'interdit par une contrainte d'exclusion. On garde donc la première
 * trouvée sans arbitrer.
 */
export function attelages(
  vehicules: readonly LigneParc[],
  affectations: readonly Affectation[],
  jour: string,
): readonly Attelage[] {
  const enCours = affectations.filter((a) => couvre(a, jour));

  return vehicules
    .filter((v) => v.actif)
    .map((vehicule) => {
      const siennes = enCours.filter((a) => a.vehicule_id === vehicule.id);
      const chauffeur = siennes.find((a) => a.genre === 'chauffeur');
      const remorque = siennes.find((a) => a.genre === 'remorque');
      return {
        vehicule,
        ...(chauffeur === undefined ? {} : { chauffeur }),
        ...(remorque === undefined ? {} : { remorque }),
      };
    });
}

/** L'attelage d'un véhicule donné, ou `undefined` s'il n'est pas de la liste. */
export function attelageDe(
  liste: readonly Attelage[],
  vehiculeId: string,
): Attelage | undefined {
  return liste.find((a) => a.vehicule.id === vehiculeId);
}

// ---------------------------------------------------------------------------
// Ce qui est libre
// ---------------------------------------------------------------------------

/**
 * Pourquoi une ressource n'est pas proposée. Rendue plutôt que gardée pour soi :
 * « pourquoi GK-833-MX n'est-il pas dans la liste » est la première question
 * qu'on se pose devant un choix qui manque.
 */
export type Empechement = 'attele' | 'absent' | 'inactif';

export interface Disponibilite<T> {
  readonly ressource: T;
  /** Absent si la ressource est libre. */
  readonly empechement?: Empechement;
  /** Le motif de l'arrêt, quand c'est lui qui empêche. */
  readonly motif?: string;
}

function arretee(
  indisponibilites: readonly Indisponibilite[],
  colonne: 'chauffeur_id' | 'vehicule_id' | 'remorque_id',
  id: string,
  jour: string,
): Indisponibilite | undefined {
  return indisponibilites.find((i) => i[colonne] === id && couvre(i, jour));
}

/**
 * L'état de chaque ressource d'un parc au jour dit : libre, ou empêchée et
 * pourquoi.
 *
 * `colonneAffectation` dit quelle colonne d'une affectation désigne cette
 * ressource — `vehicule_id`, `chauffeur_id` ou `remorque_id`. Les trois parcs
 * passent par la même fonction, et c'est ce qui garantit qu'on n'oubliera pas
 * les arrêts sur l'un des trois.
 */
export function disponibilites(
  ressources: readonly LigneParc[],
  affectations: readonly Affectation[],
  indisponibilites: readonly Indisponibilite[],
  colonne: 'chauffeur_id' | 'vehicule_id' | 'remorque_id',
  jour: string,
): readonly Disponibilite<LigneParc>[] {
  const enCours = affectations.filter((a) => couvre(a, jour));

  return ressources.map((ressource) => {
    if (!ressource.actif) {
      return { ressource, empechement: 'inactif' as const };
    }
    const arret = arretee(indisponibilites, colonne, ressource.id, jour);
    if (arret !== undefined) {
      return { ressource, empechement: 'absent' as const, motif: arret.motif };
    }
    if (enCours.some((a) => a[colonne] === ressource.id)) {
      return { ressource, empechement: 'attele' as const };
    }
    return { ressource };
  });
}

/** Celles qu'on peut atteler, dans l'ordre reçu. */
export function libres(
  etats: readonly Disponibilite<LigneParc>[],
): readonly LigneParc[] {
  return etats.filter((d) => d.empechement === undefined).map((d) => d.ressource);
}

// ---------------------------------------------------------------------------
// Ce que l'écran annonce
// ---------------------------------------------------------------------------

/** « 6 véhicules », « 1 remorque », « aucun chauffeur ». */
function compte(nombre: number, singulier: string, pluriel: string): string {
  if (nombre === 0) return `aucun${pluriel === 'remorques' ? 'e' : ''} ${singulier}`;
  return `${nombre} ${nombre > 1 ? pluriel : singulier}`;
}

/**
 * « Au 04/09/2026 : 5 remorques et 6 chauffeurs libres ».
 *
 * Une seule phrase, au-dessus des colonnes : le décompte de chacune y
 * suffirait, mais il faudrait alors additionner de tête pour savoir si l'on
 * peut composer quelque chose.
 *
 * **Pas de décompte des véhicules** : le véhicule se choisit par sa vignette,
 * pas dans une colonne. Une première version en comptait trois et retirait le
 * premier avec un `replace` — la phrase commençait alors par « aucun
 * véhicule », vu à l'écran le 03/09/2026.
 */
export function resumeLibres(
  remorques: number,
  chauffeurs: number,
  jour: string,
): string {
  return (
    `Au ${enFrancais(jour) ?? jour} : ` +
    `${compte(remorques, 'remorque', 'remorques')} et ` +
    `${compte(chauffeurs, 'chauffeur', 'chauffeurs')} libres`
  );
}

/**
 * « Au 04/09/2026 : 3 remorques libres », pour UN SEUL genre à la fois.
 *
 * Distincte de `resumeLibres`, qui en annonce deux : la boîte d'attelage n'en
 * cherche qu'un, et lui faire dire « aucune remorque et aucun chauffeur libres »
 * quand on attelle une remorque parlait de ce qu'on n'avait pas demandé. Vu à
 * l'écran le 04/09/2026.
 */
export function resumeGenreLibre(
  combien: number,
  genre: 'chauffeur' | 'remorque',
  jour: string,
): string {
  const dit =
    genre === 'remorque'
      ? compte(combien, 'remorque', 'remorques')
      : compte(combien, 'chauffeur', 'chauffeurs');
  return `Au ${enFrancais(jour) ?? jour} : ${dit} libre${combien > 1 ? 's' : ''}`;
}

/**
 * « GEORGICELLI Gérald est absent (Congés) ».
 *
 * **Les ABSENTS seulement, jamais les attelés.** Une première version nommait
 * les deux, et la phrase faisait trois lignes en récitant tout le parc — vu à
 * l'écran le 03/09/2026. Ce qui est attelé se voit dans les vignettes juste
 * au-dessus ; ce qui manque parce que quelqu'un est en congés ou qu'une
 * remorque est en carrosserie ne se voit NULLE PART ailleurs, et c'est la
 * question qu'on se pose devant un choix qui manque.
 *
 * Rend une chaîne VIDE quand personne n'est absent : l'écran n'affiche alors
 * rien plutôt qu'une phrase qui dit qu'il n'y a rien à dire.
 */
export function resumeEmpeches(
  etats: readonly Disponibilite<LigneParc>[],
  nommer: (ressource: LigneParc) => string,
): string {
  const absents = etats.filter((d) => d.empechement === 'absent');
  if (absents.length === 0) return '';

  const dits = absents.map((d) => {
    const nom = nommer(d.ressource);
    return d.motif === undefined ? nom : `${nom} (${d.motif.toLowerCase()})`;
  });

  const enumeres =
    dits.length === 1
      ? (dits[0] as string)
      : `${dits.slice(0, -1).join(', ')} et ${dits[dits.length - 1] as string}`;

  return `${enumeres} ${dits.length > 1 ? 'sont absents' : 'est absent'}`;
}

// ---------------------------------------------------------------------------
// L'échange rapide
// ---------------------------------------------------------------------------

/**
 * Peut-on échanger ces deux véhicules à cette date, et sinon pourquoi ?
 *
 * Ce contrôle ANTICIPE celui de l'API — il ne le remplace pas. L'API refuse
 * aussi, et c'est elle qui a raison si les deux divergent ; mais un bouton qui
 * s'allume pour rien fait cliquer avant de faire lire.
 *
 * Trois refus, dans l'ordre où on les rencontre :
 *
 *   - il faut deux véhicules, et deux DIFFÉRENTS ;
 *   - il faut qu'il y ait quelque chose à croiser : deux véhicules sans
 *     chauffeur ni remorque n'échangent rien ;
 *   - un attelage COMMENCÉ le jour même ne peut pas être clos la veille. C'est
 *     la règle des bornes inclusives, et l'API la fait respecter.
 */
export function refusEchange(
  a: Attelage | undefined,
  b: Attelage | undefined,
  quoi: Quoi,
  du: string,
): string | null {
  if (a === undefined || b === undefined) {
    return 'Cochez deux véhicules.';
  }
  if (a.vehicule.id === b.vehicule.id) {
    return 'Les deux véhicules doivent être différents.';
  }

  const genres: readonly ('chauffeur' | 'remorque')[] =
    quoi === 'chauffeurs'
      ? ['chauffeur']
      : quoi === 'remorques'
        ? ['remorque']
        : ['chauffeur', 'remorque'];

  const concernees = genres.flatMap((genre) =>
    [a[genre], b[genre]].filter((p): p is Affectation => p !== undefined),
  );

  if (concernees.length === 0) {
    return 'Rien à échanger à cette date.';
  }

  const laVeille = veille(du);
  const trop = concernees.find((p) => p.du > laVeille);
  if (trop !== undefined) {
    return `Un attelage commence le ${enFrancais(trop.du) ?? trop.du} : il ne peut pas être clos la veille du changement.`;
  }
  return null;
}

/** « GZ-083-NF ⇄ GX-473-ZM », le titre de la bande d'échange. */
export function titreEchange(
  a: Attelage,
  b: Attelage,
  nommer: (v: LigneParc) => string,
): string {
  return `${nommer(a.vehicule)} ⇄ ${nommer(b.vehicule)}`;
}

// ---------------------------------------------------------------------------
// Changer un attelage sans blocage — lot 94
// ---------------------------------------------------------------------------
/*
 * Ce que la route `POST /affectations/ranger` rend, et sa mise en mots.
 *
 * LE PLAN EST CALCULÉ PAR L'API, JAMAIS ICI. L'écran demande une simulation,
 * la montre, puis demande l'exécution : ce que la personne valide est donc
 * exactement ce qui sera écrit. Ce fichier ne fait que le DIRE.
 */

export type GenreAttelage = 'chauffeur' | 'remorque';

/** Une écriture du plan, avec des noms et non des identifiants. */
export interface EcritureRangement {
  readonly action:
    'creer' | 'reprendre' | 'clore' | 'decaler' | 'supprimer' | 'modifier';
  readonly vehicule: string;
  readonly genre: GenreAttelage;
  readonly ressource: string;
  readonly du: string;
  readonly au: string | null;
  readonly ancien_du: string | null;
  readonly ancien_au: string | null;
}

export type AvertissementRangement =
  | {
      readonly type: 'degarni';
      readonly vehicule: string;
      readonly genre: GenreAttelage;
      readonly du: string;
      readonly au: string | null;
    }
  | {
      readonly type: 'fin_calculee';
      readonly genre: GenreAttelage;
      readonly ressource: string;
      readonly au: string;
    }
  | {
      readonly type: 'absent';
      readonly genre: GenreAttelage;
      readonly ressource: string;
      readonly motif: string;
      readonly du: string;
      readonly au: string | null;
    };

export interface PlanRangement {
  readonly simulation: boolean;
  /** Rien à écrire : la ressource est déjà là sur toute la période. */
  readonly deja_en_place: boolean;
  readonly ecritures: readonly EcritureRangement[];
  readonly avertissements: readonly AvertissementRangement[];
}

/**
 * Ce qu'on choisit dans une liste de la fenêtre « Changer l'attelage ».
 *
 * TROIS SENS, et c'est ce qui permet de ne remplir que ce qui change :
 *   - `INCHANGE` : on ne touche pas à ce genre ;
 *   - `AUCUN` (chaîne vide) : on décroche à partir de la date ;
 *   - un identifiant : cette ressource.
 */
export const INCHANGE = '=';
export const AUCUN = '';

/** Ce que la fenêtre tient, et ce qui part à l'API. */
export interface SaisieChangement {
  readonly vehiculeId: string;
  readonly du: string;
  /** Vide = sans fin saisie. */
  readonly au: string;
  readonly chauffeur: string;
  readonly remorque: string;
}

/**
 * Ce qui empêche de demander le plan, en français — ou `null`.
 *
 * Anticipe l'API sans la remplacer : elle refuse les mêmes cas.
 */
export function refusChangement(saisie: SaisieChangement): string | null {
  if (saisie.du === '') return 'Indiquez le premier jour.';
  if (saisie.au !== '' && saisie.au < saisie.du) {
    return 'Le dernier jour ne peut pas précéder le premier.';
  }
  if (saisie.chauffeur === INCHANGE && saisie.remorque === INCHANGE) {
    return 'Choisissez un chauffeur, une remorque, ou les deux.';
  }
  return null;
}

/** Le corps de `POST /affectations/ranger`. Une clé absente = inchangé. */
export function corpsDeChangement(
  saisie: SaisieChangement,
  simuler: boolean,
): Record<string, unknown> {
  const corps: Record<string, unknown> = {
    vehicule_id: saisie.vehiculeId,
    du: saisie.du,
    simuler,
  };
  if (saisie.au !== '') corps['au'] = saisie.au;
  if (saisie.chauffeur !== INCHANGE) {
    corps['chauffeur_id'] = saisie.chauffeur === AUCUN ? null : saisie.chauffeur;
  }
  if (saisie.remorque !== INCHANGE) {
    corps['remorque_id'] = saisie.remorque === AUCUN ? null : saisie.remorque;
  }
  return corps;
}

/** « Ammid Driyej sur GZ-083-NF ». */
function quiSurQuoi(e: {
  readonly ressource: string;
  readonly vehicule: string;
}): string {
  return `${e.ressource} sur ${e.vehicule}`;
}

/** « 15/08/2026 ». */
function jourDit(iso: string): string {
  return enFrancais(iso) ?? iso;
}

/**
 * Une écriture du plan, en une phrase.
 *
 * Chaque phrase dit ce qui CHANGE et, pour ce qui existait, ce que c'était :
 * « décroché le 16/09 » ne suffit pas à savoir de quoi l'on parle.
 */
export function ecritureDite(e: EcritureRangement): string {
  const ancienne =
    e.ancien_du === null ? '' : periodeDite({ du: e.ancien_du, au: e.ancien_au });
  switch (e.action) {
    case 'creer':
      return `${quiSurQuoi(e)} : ${periodeDite(e)}`;
    case 'reprendre':
      return `${quiSurQuoi(e)} : reprend le ${jourDit(e.du)}${
        e.au === null ? '' : `, jusqu’au ${jourDit(e.au)}`
      }`;
    case 'clore':
      return `${quiSurQuoi(e)} : dernier jour le ${jourDit(e.au ?? e.du)} (était ${ancienne})`;
    case 'decaler':
      return `${quiSurQuoi(e)} : commence désormais le ${jourDit(e.du)}, au lieu du ${jourDit(
        e.ancien_du ?? e.du,
      )}`;
    case 'supprimer':
      return `${quiSurQuoi(e)} : retiré${e.genre === 'remorque' ? 'e' : ''} (était ${ancienne}, entièrement recouvert${e.genre === 'remorque' ? 'e' : ''})`;
    case 'modifier':
      return `${quiSurQuoi(e)} : désormais ${periodeDite(e)} (était ${ancienne})`;
  }
}

/** Le signe qui précède chaque écriture, pour la lire d'un coup d'œil. */
export function signeEcriture(e: EcritureRangement): string {
  switch (e.action) {
    case 'creer':
      return '+';
    case 'reprendre':
      return '↻';
    case 'clore':
      return '✂';
    case 'decaler':
      return '→';
    case 'supprimer':
      return '−';
    case 'modifier':
      return '✎';
  }
}

/** Un avertissement du plan, en une phrase. */
export function avertissementDit(a: AvertissementRangement): string {
  switch (a.type) {
    case 'degarni': {
      const quoi = a.genre === 'chauffeur' ? 'chauffeur' : 'remorque';
      const quand =
        a.au === null
          ? `à partir du ${jourDit(a.du)}`
          : `du ${jourDit(a.du)} au ${jourDit(a.au)}`;
      return `${a.vehicule} se retrouve sans ${quoi} ${quand}.`;
    }
    case 'fin_calculee':
      return `La période de ${a.ressource} s’arrête le ${jourDit(a.au)} : un autre attelage est déjà programmé le lendemain.`;
    case 'absent': {
      const etat = a.genre === 'chauffeur' ? 'absent' : 'indisponible';
      return `${a.ressource} est ${etat} (${a.motif.toLowerCase()}) ${periodeDite(a)}. L’attelage sera enregistré quand même.`;
    }
  }
}

/**
 * Ce qu'on sait d'une ressource à une date, pour l'écrire à côté de son nom
 * dans la liste — ou `null` si elle est libre.
 *
 * RIEN N'EST RETIRÉ DE LA LISTE, et c'est le point du lot 94 : une ressource
 * attelée ailleurs ou absente se choisit quand même. On le dit, c'est tout ;
 * le plan dira ce que ça entraîne.
 */
export function noteDeRessource(
  ressourceId: string,
  colonne: 'chauffeur_id' | 'remorque_id',
  vehiculeId: string,
  affectations: readonly Affectation[],
  indisponibilites: readonly Indisponibilite[],
  jour: string,
): string | null {
  const notes: string[] = [];
  const attelee = affectations.find(
    (a) => a[colonne] === ressourceId && couvre(a, jour),
  );
  if (attelee !== undefined) {
    notes.push(
      attelee.vehicule_id === vehiculeId
        ? 'déjà sur ce camion'
        : `sur ${attelee.vehicule}`,
    );
  }
  const arret = indisponibilites.find(
    (i) => i[colonne] === ressourceId && couvre(i, jour),
  );
  if (arret !== undefined) {
    notes.push(
      `${colonne === 'chauffeur_id' ? 'absent' : 'indisponible'} (${arret.motif.toLowerCase()})`,
    );
  }
  return notes.length === 0 ? null : notes.join(', ');
}
